import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type LanguageSwitcherLocale } from '@bbkAdminComponents/designer/language-switcher';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  useGetApplicationLocalesQuery,
  useGetAvailableLocalesQuery,
} from '@bbkAdminRedux/rtkq/languages.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';

type UseLanguageSwitcherLocalesOptions = {
  /** When true, only enabled locales are returned. Defaults to false. */
  enabledOnly?: boolean;
};

/** Maps application locales to `LanguageSwitcherLocale[]` (primary first). */
export const useLanguageSwitcherLocales = ({
  enabledOnly = false,
}: UseLanguageSwitcherLocalesOptions = {}): LanguageSwitcherLocale[] => {
  const appKey = useSelector(selectAppId);
  const isMultiLanguage = useFeatureFlags(FeatureFlagsE.MULTI_LANGUAGE);
  const { data: appLocales = [] } = useGetApplicationLocalesQuery(
    { appKey },
    { skip: !isMultiLanguage }
  );
  const { data: availableLocales = [] } = useGetAvailableLocalesQuery(
    undefined,
    { skip: !isMultiLanguage }
  );

  return useMemo(() => {
    const nameFor = (code: string) =>
      availableLocales.find((a) => a.locale_code === code)?.display_name ??
      code;
    return [...appLocales]
      .filter((l) => (enabledOnly ? l.enabled : true))
      .sort((a, b) => (a.base === b.base ? 0 : a.base ? -1 : 1))
      .map((l) => ({
        code: l.locale_code,
        name: nameFor(l.locale_code),
        isPrimary: l.base,
        enabled: l.enabled,
      }));
  }, [appLocales, availableLocales, enabledOnly]);
};
